import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { helpApi, type HelpKnownIssue } from '../../lib/api'

function formatWhen(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date)
}

function scheduleText(endsAt: string | null): string {
  if (!endsAt) return 'No end time'
  return `Until ${formatWhen(endsAt)}`
}

interface KnownIssueBannerProps {
  linkable?: boolean
  selectedIds?: string[]
  onToggle?: (articleId: string, checked: boolean) => void
}

export function KnownIssueBanner({ linkable = false, selectedIds = [], onToggle }: KnownIssueBannerProps) {
  // Cached for 30 seconds. An open page can keep a pin after ends_at
  // until this query refetches. React Query also refetches when the
  // window regains focus.
  const knownIssues = useQuery({
    queryKey: ['help-known-issues'],
    queryFn: () => helpApi.knownIssues(),
    retry: false,
    staleTime: 30_000,
  })

  const issues = knownIssues.data?.data ?? []
  if (!knownIssues.isSuccess || issues.length === 0) return null

  return (
    <section aria-label="Known issues" className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3">
      <h2 className="text-sm font-semibold text-amber-950">Known issues</h2>
      <p className="mt-1 text-xs text-amber-900">Times are shown in your time zone.</p>
      <ul className="mt-2 space-y-2">
        {issues.map((issue: HelpKnownIssue) => (
          <li key={issue.id} className="text-sm text-amber-950">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <Link
                to={`/help/${issue.slug}`}
                target={linkable ? '_blank' : undefined}
                rel={linkable ? 'noreferrer' : undefined}
                className="font-medium underline"
              >
                {issue.title}
              </Link>
              <span>{scheduleText(issue.ends_at)}</span>
            </div>
            {linkable ? (
              <label className="mt-1 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(issue.article_id)}
                  onChange={(event) => onToggle?.(issue.article_id, event.target.checked)}
                />
                {`Link this ticket to ${issue.title}`}
              </label>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  )
}
